import * as SecurityService from 'es/app/business-logic/services/security';
import ApplicationError, { ErrorSeverity } from 'es/app/business-logic/ApplicationError';
import HttpOperationError from 'es/app/business-logic/HttpOperationError';
import { GlobalStores } from 'es/app/store/GlobalStore';
import AppSettings from 'es/core/AppSettings';
import { createl10n } from 'es/i18n';
import isNil from 'lodash/fp/isNil';
import isObject from 'lodash/fp/isObject';
import { SITE_ENTITY_SEARCH_BY_ID } from 'es/app/business-logic/services/universal-search/graphql/facility.siteEntitySearchById';
import { FETCH_PROCESSES } from 'es/app/business-logic/services/process/graphql/facility.fetchProcesses';
import { FETCH_FACILITY_LOCATIONS_VIA_MONITORS } from 'es/app/business-logic/services/facility/graphql/system.fetchFacilityLocationsViaMonitors';

import urlJoin from 'url-join';

import {
    ApolloClient,
    ApolloClientOptions,
    ApolloLink,
    createHttpLink,
    DataProxy,
    InMemoryCache,
    NormalizedCacheObject,
    PossibleTypesMap,
    ServerError,
    ServerParseError,
    from,
    ApolloQueryResult,
    OperationVariables,
    QueryOptions,
    ApolloCache,
    DefaultContext,
    FetchResult,
    MutationOptions,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { ApolloError } from '@apollo/client/errors';

import AuthParameters from './AuthParameters';
// Apollo can't work out the concrete type to use when an Interface type is returned from the graph, so we have to
// tell it how to do it. https://www.apollographql.com/docs/react/data/fragments/#fragments-on-unions-and-interfaces
import possibleTypes from './global.possible-types.json';
import { extractGraphQlErrors, raiseGraphErrorIfUnhandled, raiseGraphWarning } from './GraphError';
import { DefinitionNode, DocumentNode } from 'graphql';
import LogManager from '../logging/LogManager';
import { raiseNetworkError } from './NetworkError';

const t = createl10n('core', 'net.apolloProvider');

const logger = LogManager.getLogger('ApolloProvider');

let rootClient: DefaultClient;
let companyClient: CompanyClient;
let facilityClient: FacilityClient;

function isServerError(error: Error | ServerParseError | ServerError): error is ServerError {
    return 'result' in error;
}

function isServerErrorWithRecord(
    result: string | Record<string, string>
): result is { message?: string; exceptionMessage?: string; stackTrace?: string } {
    const { message, exceptionMessage, stackTrace } = result as {
        message?: unknown;
        exceptionMessage?: unknown;
        stackTrace?: unknown;
    };
    return typeof message === 'string' || typeof exceptionMessage === 'string' || typeof stackTrace === 'string';
}
function isServerParseError(error: Error | ServerParseError | ServerError): error is ServerParseError {
    return 'bodyText' in error;
}

export const extractApolloErrors = (apolloErrors: ApolloError): string[] => {
    const distinctErrorMessages = new Set<string>();
    distinctErrorMessages.add(apolloErrors.message);
    const { networkError } = apolloErrors;
    if (!networkError) return [...distinctErrorMessages.values()];
    if (isServerError(networkError)) {
        if (isServerErrorWithRecord(networkError.result)) {
            const message = networkError.result.message + ' ' + networkError.result.exceptionMessage;
            distinctErrorMessages.add(message);
            if (networkError.result.stackTrace) {
                distinctErrorMessages.add(networkError.result.stackTrace);
            }
        }
    }
    if (isServerParseError(networkError)) {
        distinctErrorMessages.add(networkError?.bodyText);
    }
    return [...distinctErrorMessages.values()];
};

function raiseApolloErrorIfUnhandled(errors: ApolloError, operationName: string) {
    const details = extractApolloErrors(errors).join('\n') || t('unknownError');
    const graphErrors = extractGraphQlErrors(errors.graphQLErrors).join('\n');

    const errorText = operationName ? t('errors.namedOperationFailed', { operationName }) : t('errors.operationFailed');

    const error = new HttpOperationError(errorText, details);

    // If nothing marks the error as handled by the time the queue gets back to processing this handler, then
    // raise this as an application error.
    setTimeout(() => {
        if (error.handled) return;
        ApplicationError.raiseError(error.message, graphErrors + details, 'GraphQL', ErrorSeverity.Error);
    });

    throw error;
}

class DefaultClient extends ApolloClient<NormalizedCacheObject> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async query<T = any, TVariables extends OperationVariables = OperationVariables>(
        options: QueryOptions<TVariables, T>
    ): Promise<ApolloQueryResult<T>> {
        return super.query<T, TVariables>({
            ...options,
            errorPolicy: options.errorPolicy ?? 'all',
            fetchPolicy: options.fetchPolicy ?? 'no-cache',
        });
    }

    async mutate<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        TData = any,
        TVariables extends OperationVariables = OperationVariables,
        TContext extends Record<string, any> = DefaultContext,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    >(options: MutationOptions<TData, TVariables, TContext, ApolloCache<any>>): Promise<FetchResult<TData>> {
        return await super.mutate<TData, TVariables, TContext>({
            ...options,
            errorPolicy: options.errorPolicy ?? 'all',
            fetchPolicy: options.fetchPolicy ?? 'no-cache',
        });
    }
}

class CompanyClient extends DefaultClient {
    constructor(
        options: ApolloClientOptions<NormalizedCacheObject>,
        public companyAlias: string
    ) {
        super(options);
    }
}

class FacilityClient extends CompanyClient {
    constructor(
        options: ApolloClientOptions<NormalizedCacheObject>,
        public companyAlias: string,
        public facilityAlias: string
    ) {
        super(options, companyAlias);
    }
}

// We want to do this to maintain a link between the generated code and this ignore logic (ie if someone renames it, it will blow up and alert them to change things here)
const ignoreErrorsForDefinitions = [
    SITE_ENTITY_SEARCH_BY_ID,
    FETCH_PROCESSES,
    FETCH_FACILITY_LOCATIONS_VIA_MONITORS,
].flatMap(extractOperationNameFromDocumentNode);

function isOperationDefinition(definition: DefinitionNode) {
    return definition.kind === 'OperationDefinition' ? definition : [];
}

function extractOperationNameFromDocumentNode({ definitions }: DocumentNode) {
    const [operation] = definitions.flatMap(isOperationDefinition);
    return operation?.name?.value ?? null;
}

const buildClientOptions = (
    endpoint: string,
    companyAlias?: string,
    facilityAlias?: string,
    possibleTypes?: PossibleTypesMap
): ApolloClientOptions<NormalizedCacheObject> => {
    const authLink = new ApolloLink((operation, forward) => {
        const headers = AuthParameters.createRequestHeaders(companyAlias, facilityAlias);
        operation.setContext({ headers });
        return forward(operation);
    });

    const createNetworkErrorLink = () => {
        return onError(({ networkError }) => {
            if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
                logger.error('Unauthorised request, redirecting to login page');
                logger.error(`[Network error]: ${networkError}`);
                window.location.href = SecurityService.fetchLoginUrl(window.location.href);
            } else if (networkError) {
                logger.error(`[Network error]: ${networkError}`);
                raiseNetworkError(networkError);
            }
        });
    };

    const errorLink = new ApolloLink((operation, forward) => {
        return forward(operation).map(result => {
            const [definition] = operation.query.definitions.flatMap(isOperationDefinition);
            const operationName = getOperationName(definition);
            if (ignoreErrorsForDefinitions.includes(operationName)) {
                return result;
            }

            try {
                if (result.errors) {
                    if (!!result.data) {
                        // If we got back some data then just show the errors as a warning. This allows the UI to display
                        // what it can but still notifies the user that there was a problem.
                        raiseGraphWarning(result.errors, operationName);
                    } else {
                        // Otherwise if we didn't get back ANY data then treat this as a more severe error.
                        raiseGraphErrorIfUnhandled(result.errors, operationName);
                    }
                }
            } catch (error) {
                raiseApolloErrorIfUnhandled(error as ApolloError, operationName);
            }
            return result;
        });
    });

    const createNamedLink = () => {
        const uri: string = AppSettings.graphQLUrl;
        return new ApolloLink((operation, forward) => {
            operation.setContext(() => ({
                uri: `${uri}?${operation.operationName}`,
            }));
            return forward ? forward(operation) : null;
        });
    };

    const uri: string = urlJoin(AppSettings.graphQLUrl, endpoint);
    const httpLink = createHttpLink({ uri, credentials: 'include' });
    const networkErrorLink = createNetworkErrorLink();
    const namedLink = createNamedLink();

    return {
        link: from([authLink, networkErrorLink, errorLink, namedLink, httpLink]),
        cache: new InMemoryCache({
            possibleTypes,
        }),
    };
};

function getOperationName(definition?: DefinitionNode) {
    const fallbackOperationName = 'Unknown operation name';
    if (definition && 'operation' in definition) return definition?.name?.value ?? fallbackOperationName;
    return fallbackOperationName;
}

export default class ApolloProvider {
    // This can be deleted
    public static get system(): DefaultClient {
        return rootClient || (rootClient = new DefaultClient(buildClientOptions('')));
    }

    // This can be deleted
    public static get lookup(): DefaultClient {
        return this.facility();
    }

    // This can be renamed to Global
    public static get root(): DefaultClient {
        return rootClient || (rootClient = new DefaultClient(buildClientOptions('')));
    }

    // This can be deleted, I think?  Filters should mean we can remove this
    public static company(companyAlias?: string): CompanyClient {
        if (!companyAlias) {
            companyAlias = this.getAuthDomain().companyAlias;
        }

        if (!companyAlias) throw new Error('The companyAlias is missing.');

        if (companyClient && companyClient.companyAlias === companyAlias) {
            return companyClient;
        }

        const options = buildClientOptions(`?companyAlias=${companyAlias}`, companyAlias);
        return (companyClient = new CompanyClient(options, companyAlias));
    }

    // Only to be used when access a facilities data
    public static facility(companyAlias?: string, facilityAlias?: string): FacilityClient {
        if (!(companyAlias && facilityAlias)) {
            const authDomain = this.getAuthDomain();
            companyAlias = authDomain.companyAlias;
            facilityAlias = authDomain.facilityAlias;
        }

        if (!(companyAlias && facilityAlias)) throw new Error('The companyAlias and/or facilityAlias is missing.');

        if (
            facilityClient &&
            facilityClient.companyAlias === companyAlias &&
            facilityClient.facilityAlias === facilityAlias
        ) {
            return facilityClient;
        }

        const options = buildClientOptions(
            `?companyAlias=${companyAlias}&facilityAlias=${facilityAlias}`,
            companyAlias,
            facilityAlias,
            possibleTypes
        );

        return (facilityClient = new FacilityClient(options, companyAlias, facilityAlias));
    }

    private static getAuthDomain(): { companyAlias: string; facilityAlias: string } {
        const { activeSite } = GlobalStores.userStore;

        if (!activeSite) throw new Error('No active site');

        return {
            companyAlias: activeSite.companyAlias,
            facilityAlias: activeSite.facilityAlias,
        };
    }
}

export interface ITypedGraphQLNode {
    __typename?: string;
}

export function removeTypeNameFromNode<T extends Record<string, any>>(source: T): Omit<T, '__typename'> {
    if (isNil(source)) return source;
    if (!isObject(source)) return source;

    const result: Record<string, any> = {};

    Object.keys(source).forEach(key => {
        if (key === '__typename') return;

        const value = source[key];
        if (isNil(value)) {
            result[key] = value;
        } else if (Array.isArray(value)) {
            result[key] = value.map(removeTypeNameFromNode);
        } else {
            result[key] =
                typeof value === 'object' && value !== null
                    ? removeTypeNameFromNode(value as Record<string, unknown>)
                    : value;
        }
    });

    return result as Omit<T, '__typename'>;
}

export function expireCacheAfterUpdate(testKey: (key: string) => boolean) {
    return (proxy: DataProxy) =>
        // @ts-ignore - TODO: fix
        Object.keys(proxy.data.data)
            .filter(testKey)
            // @ts-ignore - TODO: fix
            .forEach(key => proxy.data.delete(key));
}
